import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Compass, ChevronRight, Check, Dumbbell, Brain, Users, Target, Gamepad2, PiggyBank, Clock, ArrowRight } from 'lucide-react';

const MOOD_OPTIONS = [
  { key: 'stressed', emoji: '😟', label: 'Stressed' },
  { key: 'bored', emoji: '😑', label: 'Bored' },
  { key: 'anxious', emoji: '😰', label: 'Anxious' },
  { key: 'lonely', emoji: '😢', label: 'Lonely' },
  { key: 'frustrated', emoji: '😠', label: 'Frustrated' },
];

const CATEGORY_ICONS = {
  physical: Dumbbell, mindfulness: Brain, social: Users,
  productive: Target, entertainment: Gamepad2, financial: PiggyBank,
};

export default function AlternativeActivities() {
  const [categories, setCategories] = useState({});
  const [stats, setStats] = useState(null);
  const [selectedCat, setSelectedCat] = useState(null);
  const [activeActivity, setActiveActivity] = useState(null);
  const [preMood, setPreMood] = useState('');
  const [postMood, setPostMood] = useState('');
  const [stillWantToGamble, setStillWantToGamble] = useState(null);
  const [phase, setPhase] = useState('browse'); // browse, doing, postcheck
  const [timer, setTimer] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/tools/alternatives/activities'),
      api.get('/tools/alternatives/stats'),
    ]).then(([actRes, statRes]) => {
      setCategories(actRes.data.categories);
      setStats(statRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!timerRunning) return;
    const iv = setInterval(() => setTimer(t => t + 1), 1000);
    return () => clearInterval(iv);
  }, [timerRunning]);

  const startActivity = (catKey, activity) => {
    setSelectedCat(catKey);
    setActiveActivity(activity);
    setPhase('doing');
    setTimer(0);
    setTimerRunning(true);
  };

  const completeActivity = () => {
    setTimerRunning(false);
    setPhase('postcheck');
  };

  const submitLog = async () => {
    try {
      await api.post('/tools/alternatives/log', {
        trigger_source: 'manual',
        activity_category: selectedCat,
        activity_id: activeActivity.id,
        activity_name: activeActivity.name,
        completed: true,
        duration_minutes: Math.max(1, Math.round(timer / 60)),
        pre_mood: preMood,
        post_mood: postMood,
        still_wanted_to_gamble: stillWantToGamble === true,
      });
      toast.success('Activity logged!');
      // Refresh stats
      const statRes = await api.get('/tools/alternatives/stats');
      setStats(statRes.data);
    } catch { toast.error('Failed to log'); }
    setPhase('browse');
    setActiveActivity(null);
    setPreMood('');
    setPostMood('');
    setStillWantToGamble(null);
  };

  const formatTimer = (secs) => `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;

  if (loading) return <div className="max-w-[1280px] mx-auto px-4 py-20 text-center"><p className="text-[#64748b]">Loading...</p></div>;

  // Active Activity Phase
  if (phase === 'doing' && activeActivity) {
    return (
      <div className="max-w-md mx-auto px-4 pt-20 pb-12 text-center" data-testid="activity-doing">
        <div className="ciq-card animate-fade-in">
          <p className="text-xs text-[#a0a8b8] uppercase tracking-wider mb-4">You're doing great!</p>
          <h2 className="text-xl font-bold text-white mb-2">{activeActivity.name}</h2>
          <p className="text-sm text-[#a0a8b8] mb-6">{activeActivity.description}</p>
          <div className="w-24 h-24 rounded-full bg-[#4d7cfe]/10 flex items-center justify-center mx-auto mb-4">
            <p className="text-2xl font-bold font-mono text-[#4d7cfe]" data-testid="activity-timer">{formatTimer(timer)}</p>
          </div>
          <p className="text-xs text-[#64748b] mb-6">Suggested: {activeActivity.duration} minutes</p>
          <button onClick={completeActivity} className="h-12 px-8 rounded-xl bg-[#10b981] text-white font-medium text-sm btn-lift" data-testid="activity-complete-btn">
            <Check className="h-4 w-4 inline mr-2" /> I'm Done
          </button>
        </div>
      </div>
    );
  }

  // Post-Activity Check
  if (phase === 'postcheck') {
    return (
      <div className="max-w-md mx-auto px-4 pt-20 pb-12" data-testid="activity-postcheck">
        <div className="ciq-card animate-slide-up space-y-5">
          <h2 className="text-lg font-bold text-white text-center">How do you feel now?</h2>
          <div className="flex gap-2 justify-center">
            {[...MOOD_OPTIONS, { key: 'better', emoji: '😊', label: 'Better' }, { key: 'great', emoji: '😄', label: 'Great' }].map(m => (
              <button key={m.key} onClick={() => setPostMood(m.key)}
                className={`p-2 rounded-lg text-center transition-all ${postMood === m.key ? 'bg-white/10 ring-1 ring-white/20 scale-110' : 'bg-[#1a1f2e]'}`}>
                <span className="text-xl">{m.emoji}</span>
                <p className="text-[10px] text-[#64748b] mt-1">{m.label}</p>
              </button>
            ))}
          </div>
          <div>
            <p className="text-sm text-[#a0a8b8] mb-2 text-center">Still want to gamble?</p>
            <div className="flex gap-3 justify-center">
              <button onClick={() => setStillWantToGamble(false)}
                className={`px-5 py-2 rounded-lg text-sm font-medium ${stillWantToGamble === false ? 'bg-[#10b981]/15 text-[#10b981] border border-[#10b981]/30' : 'bg-[#1a1f2e] text-[#64748b] border border-white/10'}`}
                data-testid="gamble-no">No</button>
              <button onClick={() => setStillWantToGamble(true)}
                className={`px-5 py-2 rounded-lg text-sm font-medium ${stillWantToGamble === true ? 'bg-[#ef4444]/15 text-[#ef4444] border border-[#ef4444]/30' : 'bg-[#1a1f2e] text-[#64748b] border border-white/10'}`}
                data-testid="gamble-yes">Yes</button>
            </div>
          </div>
          <button onClick={submitLog} disabled={!postMood} className="w-full h-11 rounded-xl bg-[#4d7cfe] text-white font-medium text-sm btn-lift disabled:opacity-50" data-testid="activity-submit-btn">
            Save & Continue
          </button>
        </div>
      </div>
    );
  }

  // Browse Phase
  return (
    <div className="max-w-[1280px] mx-auto px-4 lg:px-8 py-8 lg:py-12" data-testid="alternatives-page">
      <nav className="flex items-center gap-2 text-sm text-[#64748b] mb-6">
        <Link to="/dashboard" className="hover:text-[#4d7cfe]">Dashboard</Link><ChevronRight className="h-3.5 w-3.5" />
        <span className="text-[#a0a8b8]">Emotional Wellness</span><ChevronRight className="h-3.5 w-3.5" /><span className="text-white">Alternatives</span>
      </nav>
      <div className="flex items-start gap-4 mb-8 animate-fade-in">
        <div className="w-12 h-12 rounded-xl bg-[#06b6d4]/15 flex items-center justify-center shrink-0"><Compass className="h-6 w-6 text-[#06b6d4]" /></div>
        <div><h1 className="text-2xl lg:text-[32px] font-bold text-white">Alternative Activity Suggester</h1><p className="text-[#a0a8b8] text-sm mt-1">Healthy alternatives when the urge to gamble hits</p></div>
      </div>

      {/* Stats Bar */}
      {stats && stats.total > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="ciq-card py-3"><p className="text-xs text-[#64748b]">Activities Done</p><p className="text-2xl font-bold text-white">{stats.total}</p></div>
          <div className="ciq-card py-3"><p className="text-xs text-[#64748b]">Completed</p><p className="text-2xl font-bold text-[#10b981]">{stats.completed}</p></div>
          <div className="ciq-card py-3"><p className="text-xs text-[#64748b]">Sessions Avoided</p><p className="text-2xl font-bold text-[#4d7cfe]">{stats.sessions_avoided}</p></div>
          <div className="ciq-card py-3"><p className="text-xs text-[#64748b]">Est. Savings</p><p className="text-2xl font-bold text-[#f59e0b]">${stats.estimated_savings}</p></div>
        </div>
      )}

      {/* Pre-Mood Select */}
      {!preMood && (
        <div className="ciq-card mb-8 max-w-lg">
          <h3 className="text-white font-semibold text-sm mb-3">What's triggering you right now?</h3>
          <div className="flex flex-wrap gap-2">
            {MOOD_OPTIONS.map(m => (
              <button key={m.key} onClick={() => setPreMood(m.key)}
                className="px-4 py-2 rounded-lg bg-[#1a1f2e] border border-white/10 text-[#a0a8b8] text-sm hover:border-white/20 flex items-center gap-2" data-testid={`premood-${m.key}`}>
                <span>{m.emoji}</span> {m.label}
              </button>
            ))}
            <button onClick={() => setPreMood('just_browsing')}
              className="px-4 py-2 rounded-lg bg-[#1a1f2e] border border-white/10 text-[#a0a8b8] text-sm hover:border-white/20">
              Just browsing
            </button>
          </div>
        </div>
      )}

      {/* Activity Categories */}
      {preMood && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-fade-in">
          {Object.entries(categories).map(([catKey, cat]) => {
            const Icon = CATEGORY_ICONS[catKey] || Target;
            return (
              <div key={catKey} className="ciq-card" data-testid={`cat-${catKey}`}>
                <div className="flex items-center gap-2 mb-3">
                  <Icon className="h-5 w-5" style={{ color: cat.color }} />
                  <h3 className="text-white font-semibold text-sm">{cat.name}</h3>
                </div>
                <div className="space-y-2">
                  {cat.activities.map(act => (
                    <button key={act.id} onClick={() => startActivity(catKey, act)}
                      className="w-full text-left p-3 rounded-lg bg-[#1a1f2e] border border-white/10 hover:border-white/20 transition-all group" data-testid={`activity-${act.id}`}>
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-[#a0a8b8] group-hover:text-white transition-colors">{act.name}</p>
                        <div className="flex items-center gap-1 text-xs text-[#64748b]"><Clock className="h-3 w-3" />{act.duration}m</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
